import { useEffect, useMemo, useState, useRef, } from "react";
import {cn, isInactiveEmp} from "../../../utils/norms";

// Combobox: single input + dynamic dropdown
// `includeInactive` opts a single instance into showing terminated staff. Defaults to
// false so every existing call site keeps the active-only roster. Used by the
// "staff to replace" dropdown in BulkElectiveModal — you reassign people who left.
export default function StaffSelect ({ value, onChange, empByIndex, disabled=false, title=null, idPrefix="combo", includeInactive=false }) {
const [open, setOpen] = useState(false);
const [query, setQuery] = useState("");
const [highlight, setHighlight] = useState(0);
const inputRef = useRef(null);
const boxRef = useRef(null);
const [openUp, setOpenUp] = useState(false);

// roster (active only)
// const all = useMemo(
//     () => [...empByIndex.values()].filter(e => e?.ACTIVE_EMPLOYEE !== false),
//     [empByIndex]
// );
const all = useMemo(
    () => {
      const rows = [...empByIndex.values()];
      return includeInactive ? rows : rows.filter(e => !isInactiveEmp(e));
    },
    [empByIndex, includeInactive]
);

// const current = all.find(e => String(e.STAFFINDEX) === String(value)) || null;
const current = all.find(e => String(cn(e, "staffindex")) === String(value)) || null;

const hasSelection = !!current;

const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return all.slice(0, 50);
    return all
    // .filter(e =>
    //     `${e.STAFFNAME} ${e.STAFFDEPARTMENT} ${e.STAFFOFFICE} ${e.LEVELDESC || e.LEVELCODE}`
    //     .toLowerCase()
    //     .includes(q)
    // )
    .filter(e => {
        const hay = `${cn(e,"staffname")} ${cn(e,"staffdepartment")} ${cn(e,"staffoffice")} ${(cn(e,"leveldesc") || cn(e,"levelcode"))}`
            .toLowerCase();
        return hay.includes(q);
    })
    .slice(0, 50);
}, [all, query]);

// choose helper
// const choose = (emp) => {
//   if (!emp) {
//     onChange(null);
//     setQuery("");
//   } else {
//     onChange(Number(emp.STAFFINDEX));
//     setQuery(emp.STAFFNAME);        // <-- show solid text after selection
//   }
//   setOpen(false);
//   setHighlight(0);
//   requestAnimationFrame(() => inputRef.current?.focus());
// };

// const togglePick = (pickedIdx) => {
//   const current = value == null ? null : String(value);
//   const next    = pickedIdx == null ? null : String(pickedIdx);

//   if (current === next) {
//     onChange(null);          // clear selection
//   } else {
//     onChange(pickedIdx);     // select new value
//   }
//   setOpen(false);            // close after click (or keep open if you prefer)
// };

// Toggle select/unselect. Accept either an employee object or an index.
const togglePick = (empOrIdx) => {
    // const pickedIdx = typeof empOrIdx === "object"
    // ? Number(empOrIdx?.STAFFINDEX)
    const pickedIdx = typeof empOrIdx === "object"
        ? Number(cn(empOrIdx,"staffindex"))
        : Number(empOrIdx);

    const cur = value == null ? null : Number(value);
    if (cur === pickedIdx) {
    // unselect
    onChange(null);
    setQuery("");                       // clear solid text in input
    } else {
    // select
    onChange(pickedIdx);
    // const name =
    //     (typeof empOrIdx === "object" && empOrIdx?.STAFFNAME) ||
    //     empByIndex.get(pickedIdx)?.STAFFNAME ||
    //     "";
    const name =
       (typeof empOrIdx === "object" && cn(empOrIdx,"staffname")) ||
        cn(empByIndex.get(pickedIdx) || {}, "staffname") ||
        "";
    setQuery(name);                     // show solid text after selection
    }

    setOpen(false);
    setHighlight(0);
    requestAnimationFrame(() => inputRef.current?.focus());
};

// recompute whether we should drop up or down when opening/resizing/scrolling
useEffect(() => {
    if (!open) return;
    const recalc = () => {
    const el = inputRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - r.bottom;
    const spaceAbove = r.top;
    // open upward if there’s less room below than ~200px and more room above
    setOpenUp(spaceBelow < 200 && spaceAbove > spaceBelow);
    };
    recalc();
    window.addEventListener("resize", recalc);
    window.addEventListener("scroll", recalc, true); // capture scrolls in ancestors
    return () => {
    window.removeEventListener("resize", recalc);
    window.removeEventListener("scroll", recalc, true);
    };
}, [open]);

// Close on outside click (but not when clicking inside the popover)
useEffect(() => {
    const onDocDown = (e) => {
    if (!boxRef.current) return;
    if (!boxRef.current.contains(e.target)) {
        setOpen(false);
        setHighlight(0);
        // don't clear query; keeps what user typed/selected visible
    }
    };
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
}, []);

const onKeyDown = (e) => {
    if (!open && (e.key.length === 1 || e.key === "Backspace")) setOpen(true);
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight(h => Math.min(h + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
    // else if (e.key === "Enter")   { e.preventDefault(); choose(filtered[highlight]); }
    else if (e.key === "Enter")   { e.preventDefault(); togglePick(filtered[highlight]); }
    else if (e.key === "Escape")  { setOpen(false); }
};

// Prevent blur-before-click when selecting with mouse
const onItemMouseDown = (e, item) => {
    e.preventDefault();
    // choose(item);
    togglePick(item);
};

// What to show in the input:
// - If user is typing (open): show the query
// - Otherwise show the selected name (solid text), or empty → placeholder says "Search staff…"
// const displayValue = open ? query : (query || current?.STAFFNAME || "");
const displayValue = open ? query : (query || cn(current || {}, "staffname") || "");

const stopIfDisabled = (e) => {
    if (!disabled) return;
    e.preventDefault();
    e.stopPropagation();
};

return (
    <div ref={boxRef} className={`combo ${open ? "open" : ""} ${hasSelection ? "chosen" : ""} ${disabled ? "is-locked" : ""}`}
        aria-disabled={disabled}
        onMouseDown={stopIfDisabled}
        onClick={stopIfDisabled}
        title={title}
    >
    <input
        ref={inputRef}
        className="combo-input"
        value={displayValue}
        disabled={disabled}
        readOnly={disabled}          // prevents typing in some browsers
        tabIndex={disabled ? -1 : 0} // cannot focus via tab
        placeholder={displayValue ? "" : "Search staff…"}  // <-- placeholder only when empty
        onChange={e => {
            if (disabled) return;
            setQuery(e.target.value);
            setOpen(true);
            setHighlight(0);
        }}
        onFocus={e => {
            if (disabled) { e.preventDefault(); return; }
            setOpen(true);
        }}
        onKeyDown={e => {
            if (disabled) { e.preventDefault(); e.stopPropagation(); return; }
            onKeyDown(e);
        }}
        aria-expanded={open}
        aria-autocomplete="list"
        aria-controls={`${idPrefix}-list`}
    />
    {open && !disabled && (
        <div id={`${idPrefix}-list`} role="listbox" className={`combo-pop ${openUp ? "up" : ""}`}>
        {filtered.length === 0 ? (
            <div className="combo-empty">No matches</div>
        ) : (
            filtered.map((e, i) => (
            // <div
            //     key={e.STAFFINDEX}
            //     role="option"
            //     aria-selected={i === highlight}
            //     className={`combo-item ${i === highlight ? "active" : ""}`}
            //     onMouseDown={(ev) => onItemMouseDown(ev, e)}
            //     onMouseEnter={() => setHighlight(i)}
            //     title={`${e.STAFFDEPARTMENT || ""} • ${e.LEVELDESC || e.LEVELCODE || ""}`}
            // >
            //     <div className="ci-name">{e.STAFFNAME}</div>
            //     <div className="ci-meta">
            //     {e.STAFFDEPARTMENT || "—"} • {e.LEVELDESC || e.LEVELCODE || "—"}
            //     </div>
            // </div>
            <div
                key={cn(e,"staffindex")}
                role="option"
                aria-selected={i === highlight}
                className={`combo-item ${i === highlight ? "active" : ""}`}
                onMouseDown={(ev) => onItemMouseDown(ev, e)}
                onMouseEnter={() => setHighlight(i)}
                title={`${cn(e,"staffdepartment") || ""} • ${cn(e,"leveldesc") || cn(e,"levelcode") || ""}`}
               >
                <div className="ci-name">
                  {cn(e,"staffname")}
                  {isInactiveEmp(e) && <span className="ci-inactive-badge">inactive</span>}
                </div>
                <div className="ci-meta">
                  {cn(e,"staffdepartment") || "—"} • {cn(e,"leveldesc") || cn(e,"levelcode") || "—"}
                </div>
            </div>
            ))
        )}
        </div>
    )}
    </div>
);
};